import React, { useState } from 'react'
import {
    Box,
    CircularProgress,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import FolderOpenIcon from '@material-ui/icons/FolderOpen'
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks'
import BusinessIcon from '@material-ui/icons/Business'
import AddIcon from '@material-ui/icons/Add'
import TouchAppOutlinedIcon from '@material-ui/icons/TouchAppOutlined'

import { ConfigOptionCard } from './ConfigOptionCard'
import { NameConfigDialog } from './NameConfigDialog'
import {
    SeasonConfigBundleOption,
    bundleStatusLabel,
    sortExistingBundleOptions,
    starterApiDefinition,
} from './seasonConfigBundle'
import {
    LEASING_SEASON_CONFIG_CONTENT_MAX_WIDTH,
    formatModifiedLabel,
} from './setupArtifacts'
import { SECTION_TONES } from './setupChooserTones'
import type {
    ApiCreatedFrom,
    ApiLeasingSeasonConfigDefinition,
} from './useLeasingSeasonConfigApi'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        width: '100%',
        height: '100%',
        minHeight: 0,
        boxSizing: 'border-box',
        padding: theme.spacing(0.5, 1, 1),
        display: 'flex',
        justifyContent: 'center',
        overflow: 'hidden',
    },
    panel: {
        width: '100%',
        maxWidth: LEASING_SEASON_CONFIG_CONTENT_MAX_WIDTH,
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #D5DBE3',
        borderRadius: 12,
        backgroundColor: theme.palette.background.paper,
        overflow: 'hidden',
        boxSizing: 'border-box',
    },
    panelHeader: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1.25),
        padding: theme.spacing(1.25, 1.5),
        borderBottom: '1px solid #E8ECF1',
        backgroundColor: theme.palette.grey[50],
    },
    headerIcon: {
        flexShrink: 0,
        width: 28,
        height: 28,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#ECEFF1',
        color: '#455A64',
    },
    headerTitle: {
        fontSize: '0.8125rem',
        fontWeight: 700,
        lineHeight: 1.35,
    },
    headerDescription: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.45,
        marginTop: 2,
    },
    loading: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        minHeight: 200,
    },
    grid: {
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: theme.spacing(1),
        padding: theme.spacing(1.25),
        alignContent: 'start',
    },
    lane: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.75),
        minWidth: 0,
        border: '1px solid #E8ECF1',
        borderRadius: 10,
        padding: theme.spacing(1),
        backgroundColor: theme.palette.grey[50],
    },
    laneHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
    },
    laneIcon: {
        width: 24,
        height: 24,
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& svg': { fontSize: 16 },
    },
    laneTitle: {
        fontSize: '0.75rem',
        fontWeight: 700,
    },
    laneHint: {
        fontSize: '0.6875rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.35,
    },
    laneList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.75),
    },
    empty: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        fontStyle: 'italic',
        padding: theme.spacing(0.5, 0),
    },
}))

type PendingCreate =
    | { kind: 'scratch' }
    | { kind: 'ezos'; option: SeasonConfigBundleOption }
    | { kind: 'org'; option: SeasonConfigBundleOption }

function isSeasonLocked(option: SeasonConfigBundleOption): boolean {
    if (option.orgMandated) return false
    return option.isEditable === false
}

/**
 * Root chooser: pick or create a Leasing Season Config (bundle of six slots).
 */
export const SeasonConfigBundleChooser: React.FC<{
    existing: SeasonConfigBundleOption[]
    loading?: boolean
    submitting?: boolean
    onSelectExisting: (configId: number) => void
    onCreate: (payload: {
        name: string
        created_from: ApiCreatedFrom
        definition: ApiLeasingSeasonConfigDefinition
        description?: string
    }) => void
}> = ({
    existing,
    loading = false,
    submitting = false,
    onSelectExisting,
    onCreate,
}) => {
    const classes = useStyles()
    const [pending, setPending] = useState<PendingCreate | null>(null)
    const sortedExisting = sortExistingBundleOptions(existing)

    // Bundle-level EZOS / org templates are not shipped yet.
    const ezos: SeasonConfigBundleOption[] = []
    const org: SeasonConfigBundleOption[] = []

    const confirmCreate = (name: string) => {
        if (!pending) return
        if (pending.kind === 'scratch') {
            onCreate({
                name,
                created_from: 'scratch',
                definition: starterApiDefinition('scratch'),
            })
        } else if (pending.kind === 'ezos') {
            onCreate({
                name,
                created_from: 'ezos',
                description: pending.option.description,
                definition: starterApiDefinition('ezos'),
            })
        } else {
            onCreate({
                name,
                created_from: 'org',
                description: pending.option.description,
                definition: starterApiDefinition('org'),
            })
        }
        setPending(null)
    }

    return (
        <Box className={classes.root}>
            <Box className={classes.panel}>
                <Box className={classes.panelHeader}>
                    <Box className={classes.headerIcon}>
                        <TouchAppOutlinedIcon style={{ fontSize: 18 }} />
                    </Box>
                    <Box>
                        <Typography className={classes.headerTitle}>
                            Choose a Leasing Season Config
                        </Typography>
                        <Typography className={classes.headerDescription}>
                            A leasing season config is one named combination of
                            Qualifying Methods, Applicant Data, Guest Card,
                            Application Fee, Signable Documents, and Leads
                            Pipeline. Seasons apply this whole config.
                        </Typography>
                    </Box>
                </Box>

                {loading ? (
                    <Box className={classes.loading}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <Box className={classes.grid}>
                        <Box className={classes.lane}>
                            <Box className={classes.laneHeader}>
                                <Box
                                    className={classes.laneIcon}
                                    style={{
                                        backgroundColor:
                                            SECTION_TONES.existing.iconBg,
                                        color: SECTION_TONES.existing.iconColor,
                                    }}
                                >
                                    <FolderOpenIcon />
                                </Box>
                                <Typography className={classes.laneTitle}>
                                    Edit existing
                                </Typography>
                            </Box>
                            <Typography className={classes.laneHint}>
                                Drafts and configs attached to a season
                            </Typography>
                            <Box className={classes.laneList}>
                                {sortedExisting.length === 0 ? (
                                    <Typography className={classes.empty}>
                                        No leasing season configs yet.
                                    </Typography>
                                ) : (
                                    sortedExisting.map((option) => {
                                        const locked = isSeasonLocked(option)
                                        const modified = formatModifiedLabel(
                                            option.modifiedAt,
                                        )
                                        const status = bundleStatusLabel(option)
                                        const meta = modified
                                            ? `${status} · ${modified}`
                                            : status
                                        return (
                                            <ConfigOptionCard
                                                key={option.id}
                                                title={option.name}
                                                description={option.description}
                                                meta={meta}
                                                muted={locked}
                                                onClick={() =>
                                                    onSelectExisting(
                                                        Number(option.id),
                                                    )
                                                }
                                            />
                                        )
                                    })
                                )}
                            </Box>
                        </Box>

                        <Box className={classes.lane}>
                            <Box className={classes.laneHeader}>
                                <Box
                                    className={classes.laneIcon}
                                    style={{
                                        backgroundColor:
                                            SECTION_TONES.ezos.iconBg,
                                        color: SECTION_TONES.ezos.iconColor,
                                    }}
                                >
                                    <LibraryBooksIcon />
                                </Box>
                                <Typography className={classes.laneTitle}>
                                    Build from EZOS
                                </Typography>
                            </Box>
                            <Typography className={classes.laneHint}>
                                Copy a starter bundle onto this property
                            </Typography>
                            <Box className={classes.laneList}>
                                {ezos.length === 0 ? (
                                    <Typography className={classes.empty}>
                                        No EZOS bundle templates yet.
                                    </Typography>
                                ) : (
                                    ezos.map((option) => (
                                        <ConfigOptionCard
                                            key={option.id}
                                            title={option.name}
                                            description={option.description}
                                            onClick={() =>
                                                setPending({
                                                    kind: 'ezos',
                                                    option,
                                                })
                                            }
                                        />
                                    ))
                                )}
                            </Box>
                        </Box>

                        <Box className={classes.lane}>
                            <Box className={classes.laneHeader}>
                                <Box
                                    className={classes.laneIcon}
                                    style={{
                                        backgroundColor:
                                            SECTION_TONES.org.iconBg,
                                        color: SECTION_TONES.org.iconColor,
                                    }}
                                >
                                    <BusinessIcon />
                                </Box>
                                <Typography className={classes.laneTitle}>
                                    Build from org template
                                </Typography>
                            </Box>
                            <Typography className={classes.laneHint}>
                                Adopt org bundle (copy-on-adopt; some slots may
                                be mandated)
                            </Typography>
                            <Box className={classes.laneList}>
                                {org.length === 0 ? (
                                    <Typography className={classes.empty}>
                                        No org bundle templates yet.
                                    </Typography>
                                ) : (
                                    org.map((option) => (
                                        <ConfigOptionCard
                                            key={option.id}
                                            title={option.name}
                                            description={option.description}
                                            onClick={() =>
                                                setPending({
                                                    kind: 'org',
                                                    option,
                                                })
                                            }
                                        />
                                    ))
                                )}
                            </Box>
                        </Box>

                        <Box className={classes.lane}>
                            <Box className={classes.laneHeader}>
                                <Box
                                    className={classes.laneIcon}
                                    style={{
                                        backgroundColor:
                                            SECTION_TONES.scratch.iconBg,
                                        color: SECTION_TONES.scratch.iconColor,
                                    }}
                                >
                                    <AddIcon />
                                </Box>
                                <Typography className={classes.laneTitle}>
                                    Create your own
                                </Typography>
                            </Box>
                            <Typography className={classes.laneHint}>
                                Empty bundle — fill each slot in Setup and
                                Compose
                            </Typography>
                            <Box className={classes.laneList}>
                                <ConfigOptionCard
                                    title="Start from scratch"
                                    description="Name a config, then select each artifact"
                                    onClick={() =>
                                        setPending({ kind: 'scratch' })
                                    }
                                />
                            </Box>
                        </Box>
                    </Box>
                )}
            </Box>

            <NameConfigDialog
                open={pending != null}
                title={
                    pending?.kind === 'scratch'
                        ? 'Name your leasing season config'
                        : pending?.kind === 'org'
                        ? 'Name configuration from org template'
                        : 'Name configuration from EZOS'
                }
                initialName={
                    pending && pending.kind !== 'scratch'
                        ? pending.option.name
                        : 'Leasing Season Config'
                }
                confirmLabel="Continue"
                submitting={submitting}
                onClose={() => setPending(null)}
                onConfirm={confirmCreate}
            />
        </Box>
    )
}
